import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Form } from 'antd'
import { setupInterceptors } from 'src/Common/apiClient'
import { thumbUrl } from 'src/Common/fileAsset'
import {
  fetchProfileData,
  fetchSkillsByExpertise,
  updateProfileData,
} from 'src/Services/MentorServices/MentorProfileService'

// Custom Hook for Profile Edit functionality
function useMentorProfile() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const [skillsList, setSkillsList] = useState([])
  const [showSkillInputs, setShowSkillInputs] = useState(false)
  const [isUS, setIsUS] = useState(false)

  const token = localStorage.getItem('token')
  const navigate = useNavigate()
  setupInterceptors(navigate)

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true)
        const data = await fetchProfileData(token)

        const previewSrc = thumbUrl(data.profileImageThumbnailUrl || data.profileImageUrl)
        if (previewSrc) {
          setPreviewImage(previewSrc)
        }

        if (data.dateOfBirth && data.dateOfBirth !== '0001-01-01') {
          data.dateOfBirth = dayjs(data.dateOfBirth)
        } else {
          data.dateOfBirth = null
        }

        form.setFieldsValue({
          ...data,
          mentorSkills: (data.mentorSkills || []).map((skill) => skill.name),
        })

        // Reveal the skills / experience fields (Technical Skills, Year of Experience,
        // Mentor Experience Level) when the mentor already has an expertise saved, and
        // load that expertise's skill options so the saved skills show correctly.
        if (data.expertiseArea) {
          setShowSkillInputs(true)
          try {
            const skills = await fetchSkillsByExpertise(data.expertiseArea, token)
            setSkillsList(skills)
          } catch (e) {
            // Skill options are optional here; the saved values still display.
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [token, navigate])

  const handleExpertiseChange = async (value) => {
    form.setFieldsValue({ expertiseArea: value })
    try {
      const skills = await fetchSkillsByExpertise(value, token)
      setSkillsList(skills)
      setShowSkillInputs(true)
    } catch (error) {
      console.error('Failed to fetch skills', error)
    }
  }

  const handleCountryChange = (value) => {
    setIsUS(value === 'United States of America')
    if (value !== 'United States of America') {
      form.setFields([
        {
          name: 'zip',
          errors: [],
        },
      ])
    }
    form.validateFields(['zip'])
  }

  const handleSubmit = async (values) => {
    try {
      const formData = new FormData()
      formData.append('firstName', values.firstName)
      formData.append('middleName', values.middleName ?? '')
      formData.append('lastName', values.lastName)
      formData.append('dateOfBirth', new Date(values.dateOfBirth).toISOString().split('T')[0])
      formData.append('gender', values.gender)
      formData.append('state', values.state)
      formData.append('country', values.country)
      formData.append('expertiseArea', values.expertiseArea)

      if (values.mentorSkills) {
        values.mentorSkills.forEach((skillName) => {
          formData.append('MentorSkills', skillName)
        })
      }

      // Add experience fields if they exist
      if (values.yearsOfExperience) {
        formData.append('yearsOfExperience', values.yearsOfExperience)
      }
      if (values.mentorshipExperienceLevel) {
        formData.append('mentorshipExperienceLevel', values.mentorshipExperienceLevel)
      }

      if (values.profileImageFile?.originFileObj) {
        formData.append('profileImageFile', values.profileImageFile.originFileObj)
      }

      setLoader(true)
      const response = await updateProfileData(formData, token)
      if (response.status === 200) {
        navigate('/mentor-Dashboard', {
          state: { successMessage: 'Profile Updated Successfully!' },
        })
      }
    } catch (error) {
      console.error('Error', error)
    } finally {
      setLoader(false)
    }
  }

  return {
    form,
    previewImage,
    loading,
    loader,
    handleSubmit,
    handleCountryChange,
    isUS,
    handleExpertiseChange,
    showSkillInputs,
    skillsList,
    setPreviewImage,
  }
}

export default useMentorProfile
